/**
 * Packing fill (TD §7.2 steps 2–6), ported from the prototype's fillHabitats: frontage along the route, regions
 * (themes) as consecutive stretches of the route, spans per habitat, growth from the frontage, a majority
 * filter, and each habitat's largest piece.
 */
import { clamp } from '../geom';
import { CLS_LAND, CLS_ROAD, CLS_WATER, nbrs4, cellMid, keepLargestPieces, type Grid } from './grid';
import { nearestOnRoutes, type Route } from './routes';
import { shuffle, type Rng } from './rng';

/** Smallest piece (cells) that counts as a placed habitat; a zone that needs less only has to keep half its need (at least 4). */
export const MIN_PIECE_CELLS = 20;
/** Zones grow to this much more than they need, because the majority filter and tracing the walls trim a little. */
export const NEED_MARGIN = 1.04;
/** The same for a priority zone, which must come out at its full need. */
export const PRIORITY_MARGIN = 1.2;
/**
 * Plus this many cells per √(need): the trim is along the outline, so a small zone (long outline for its area) loses a
 * bigger share of its area than a big one.
 */
export const EDGE_MARGIN = 1.5;
/** Frontage is shared out by the room behind it, counted this deep (m) ... */
export const ROOM_DEPTH_M = 60;
/** ... plus this many cells per stretch, so a stretch with little room behind it still counts for something. */
export const ROOM_PER_STRETCH = 4;

/** One zone to place. */
export interface PackItem {
  /** Needed area in cells. */
  need: number;
  /** Water it may claim, in cells (0 for none). */
  water: number;
  /** Region (theme) it belongs to. */
  group: number;
  /** Claims its frontage and grows before every other zone (the zone "Make room" is for). */
  priority?: boolean;
}

export interface PackInput {
  g: Grid;
  /** Cell classes (CLS_*). */
  cls: Uint8Array;
  /** Roads in walking order. */
  routes: Route[];
  items: PackItem[];
  /** Regions in walking order; each item's group must be listed. */
  groups: number[];
  /** The region to rotate next to the water, or null. */
  waterGroup: number | null;
  r: Rng;
}

export interface PackResult {
  /** Item index per cell, -1 for none; every item is one 4-connected piece of at least MIN_PIECE_CELLS (or half its need if smaller), or absent. */
  lab: Int32Array;
  /** Why nothing could be placed at all, or '' when packing ran. */
  problem: string;
}

/** Packs the items along the routes. Deterministic for a given input and rng state. */
export function packAlongRoutes(inp: PackInput): PackResult {
  const { g, cls, routes, items, r } = inp, { N, n, c } = g, nb = new Int32Array(4);
  const lab = new Int32Array(n).fill(-1);
  if (!items.length) return { lab, problem: '' };
  if (!routes.length) return { lab, problem: 'Draw a path first; habitats are placed along paths.' };

  // frontage: free land next to a road, binned along the walking route and split by side
  const BIN = 6, boff: number[] = [];
  let NB = 0;
  routes.forEach((rt, i) => { boff[i] = NB; NB += Math.ceil(rt.len / BIN) + 1; });
  const front: number[][] = Array.from({ length: NB * 2 }, () => []);
  for (let k = 0; k < n; k++) {
    if (cls[k] !== CLS_LAND) continue;
    let byRoad = false;
    const m = nbrs4(g, k, nb);
    for (let t = 0; t < m; t++) if (cls[nb[t] as number] === CLS_ROAD) { byRoad = true; break; }
    if (!byRoad) continue;
    const q = nearestOnRoutes(routes, cellMid(g, k % N), cellMid(g, Math.floor(k / N)));
    if (q.ri < 0) continue;
    (front[((boff[q.ri] as number) + Math.floor(q.s / BIN)) * 2 + (q.side > 0 ? 1 : 0)] as number[]).push(k);
  }
  const fr = (g2: number, sd: number) => front[g2 * 2 + sd] as number[];
  const A: number[] = [];
  for (let b = 0; b < NB; b++) if (fr(b, 0).length || fr(b, 1).length) A.push(b);
  if (!A.length) return { lab, problem: 'No free land along the paths.' };

  // depth of the land from the frontage (zones don't grow deeper than dMax), and the room behind each stretch of
  // frontage: the land closer to it than to any other (all of it, and the part up to ROOM_DEPTH_M deep)
  const dMax = Math.round(140 / c), dRoom = Math.round(ROOM_DEPTH_M / c), dep = new Int32Array(n).fill(-1), qd: number[] = [];
  front.forEach(l => l.forEach(k => { if ((dep[k] as number) < 0) { dep[k] = 0; qd.push(k); } }));
  for (let h = 0; h < qd.length; h++) {
    const k = qd[h] as number;
    if ((dep[k] as number) >= dMax) continue;
    const m = nbrs4(g, k, nb);
    for (let t = 0; t < m; t++) { const o = nb[t] as number; if (cls[o] === CLS_LAND && (dep[o] as number) < 0) { dep[o] = (dep[k] as number) + 1; qd.push(o); } }
  }
  const room = new Float64Array(front.length), near = new Float64Array(front.length);
  {
    const from = new Int32Array(n).fill(-1), q: number[] = [];
    front.forEach((l, key) => l.forEach(k => { if ((from[k] as number) < 0) { from[k] = key; q.push(k); } }));
    for (let h = 0; h < q.length; h++) {
      const k = q[h] as number, key = from[k] as number;
      room[key] = (room[key] as number) + 1;
      if ((dep[k] as number) <= dRoom) near[key] = (near[key] as number) + 1;
      if ((dep[k] as number) >= dMax) continue;
      const m = nbrs4(g, k, nb);
      for (let t = 0; t < m; t++) { const o = nb[t] as number; if (cls[o] === CLS_LAND && (from[o] as number) < 0 && (dep[o] as number) >= 0) { from[o] = key; q.push(o); } }
    }
  }
  // a stretch counts by the land behind it when the frontage is shared out, so a zone handed a stretch along a thin
  // strip (between two paths, or a path and the lake) gets a longer stretch and isn't boxed in
  const wOf = (key: number) => (near[key] as number) + ROOM_PER_STRETCH;
  /** Index z in [lo, hi] where the running total cum (cum[0] = 0) comes closest to t. */
  const cut = (cum: number[], t: number, lo: number, hi: number) => {
    let z = lo;
    for (let x = lo; x <= hi; x++) if (Math.abs((cum[x] as number) - t) < Math.abs((cum[z] as number) - t)) z = x;
    return z;
  };
  const cumA = [0];
  A.forEach((b, x) => cumA.push((cumA[x] as number) + (fr(b, 0).length ? wOf(b * 2) : 0) + (fr(b, 1).length ? wOf(b * 2 + 1) : 0)));
  const totA = cumA[A.length] as number;

  // regions: consecutive stretches of the route, one per group; the water group is rotated to sit by the water
  const reqC = items.map(it => Math.max(25, it.need));
  const order = inp.groups.filter(gr => items.some(it => it.group === gr));
  const weight = (gr: number) => items.reduce((s, it, i) => s + (it.group === gr ? reqC[i] as number : 0), 0);
  const totalW = order.reduce((s, gr) => s + weight(gr), 0);
  const chunk = (ord: number[]) => {
    let cum = 0, prev = 0;
    return ord.map((gr, idx) => {
      cum += weight(gr);
      let z = idx === ord.length - 1 ? A.length : cut(cumA, cum / totalW * totA, 0, A.length);
      z = Math.min(A.length, Math.max(z, prev + 1));
      const res = { gr, bins: A.slice(prev, z) };
      prev = Math.max(prev, z);
      return res;
    });
  };
  let regions = chunk(order);
  const wg = inp.waterGroup;
  if (wg !== null && order.includes(wg) && order.length > 1) {
    const wd = new Int32Array(n).fill(1e6), q: number[] = [];
    for (let k = 0; k < n; k++) if (cls[k] === CLS_WATER) { wd[k] = 0; q.push(k); }
    if (q.length) {
      for (let h = 0; h < q.length; h++) {
        const k = q[h] as number, m = nbrs4(g, k, nb);
        for (let t = 0; t < m; t++) { const o = nb[t] as number; if (cls[o] && (wd[o] as number) > (wd[k] as number) + 1) { wd[o] = (wd[k] as number) + 1; q.push(o); } }
      }
      let best: { sc: number; rg: typeof regions } | null = null;
      for (let rot = 0; rot < order.length; rot++) {
        const rg = chunk(order.slice(rot).concat(order.slice(0, rot))), aq = rg.find(x => x.gr === wg);
        let s = 0, cnt = 0;
        aq?.bins.forEach(b => [0, 1].forEach(sd => fr(b, sd).forEach(k => { s += wd[k] as number; cnt++; })));
        const sc = cnt ? s / cnt : 1e9;
        if (!best || sc < best.sc) best = { sc, rg };
      }
      if (best) regions = best.rg;
    }
  }

  // inside each region: big zones to the roomier side, then contiguous frontage spans sized by area
  const seeds: number[][] = items.map(() => []), spans: number[][] = items.map(() => []);
  for (const rg of regions) {
    const hi = items.map((_, i) => i).filter(i => (items[i] as PackItem).group === rg.gr).sort((a, b) => (reqC[b] as number) - (reqC[a] as number));
    const sideBins = [0, 1].map(sd => rg.bins.filter(b => fr(b, sd).length)), load = [0, 0], onSide: number[][] = [[], []];
    for (const i of hi) {
      let bs = -1, bv = 1e18;
      for (const sd of [0, 1]) {
        const sb = sideBins[sd] as number[];
        if (sb.length) { const v = ((load[sd] as number) + (reqC[i] as number)) / sb.reduce((s, b) => s + wOf(b * 2 + sd), 0); if (v < bv) { bv = v; bs = sd; } }
      }
      if (bs >= 0) { load[bs] = (load[bs] as number) + (reqC[i] as number); (onSide[bs] as number[]).push(i); }
    }
    for (const sd of [0, 1]) {
      const list = shuffle(onSide[sd] as number[], r), tot = list.reduce((s, i) => s + (reqC[i] as number), 0), B = sideBins[sd] as number[];
      // bins per zone by area, so the room behind a zone's stretch matches its share; when there are enough, every zone
      // gets at least one of its own (zones sharing a bin fight over the same frontage and the smaller one is lost)
      const cumB = [0];
      B.forEach((b, x) => cumB.push((cumB[x] as number) + wOf(b * 2 + sd)));
      const totB = cumB[B.length] as number;
      let cum = 0, at = 0;
      list.forEach((i, t) => {
        const a0 = cut(cumB, cum / tot * totB, 0, B.length);
        cum += reqC[i] as number;
        const z0 = cut(cumB, cum / tot * totB, 0, B.length);
        const a = list.length <= B.length ? at : Math.min(B.length - 1, a0);
        const z = list.length > B.length ? Math.min(B.length, Math.max(a + 1, z0))
          : t === list.length - 1 ? B.length : clamp(z0, a + 1, B.length - (list.length - 1 - t));
        at = z;
        for (let q = a; q < z; q++) { (seeds[i] as number[]).push(...fr(B[q] as number, sd)); (spans[i] as number[]).push((B[q] as number) * 2 + sd); }
      });
    }
  }

  // grow every zone away from its frontage in two rounds: first each zone up to what it needs, then up to its share of
  // the spare land (big plots get scaled up to use it). The zone furthest behind takes the next step (a priority zone
  // goes first in the first round), so zones grow in step with their need and big neighbours can't pinch a small one off.
  const scale = clamp(qd.length * 0.8 / reqC.reduce((a, b) => a + b, 0), 1, 3);
  const margin = items.map(it => (it.priority ? PRIORITY_MARGIN : NEED_MARGIN));
  const base = reqC.map((v, i) => v * (margin[i] as number) + EDGE_MARGIN * Math.sqrt(v)), tgt = reqC.map((v, i) => Math.max(v * scale, base[i] as number)), wt = items.map(it => it.water);
  const owner = lab, depth = new Uint16Array(n), cnt = new Float64Array(items.length), wc = new Float64Array(items.length);
  /** Lays the seeds (a priority zone first, then small zones before big ones, so a small zone keeps its frontage). */
  const plant = (sd: number[][]) => {
    owner.fill(-1); depth.fill(0); cnt.fill(0); wc.fill(0);
    const order = items.map((_, i) => i).sort((a, b) => Number(!!items[b]?.priority) - Number(!!items[a]?.priority) || (reqC[a] as number) - (reqC[b] as number));
    for (const i of order) for (const k of sd[i] as number[]) if ((owner[k] as number) < 0) { owner[k] = i; cnt[i] = (cnt[i] as number) + 1; }
  };
  /** Grows the zones in `which` up to `cap`, the one furthest behind first (with `first`, a priority zone before all). */
  const grow = (which: number[], cap: number[], first: boolean): void => {
    // per zone, a queue of its cells that may still have free neighbours, in the order they were claimed
    const queues: number[][] = items.map(() => []), head = new Int32Array(items.length), on = new Uint8Array(items.length);
    for (const i of which) on[i] = 1;
    for (let k = 0; k < n; k++) { const o = owner[k] as number; if (o >= 0 && on[o]) (queues[o] as number[]).push(k); }
    for (const i of which) shuffle(queues[i] as number[], r);
    const live = which.filter(i => (cnt[i] as number) < (cap[i] as number));
    while (live.length) {
      let li = 0, bv = Infinity;
      for (let x = 0; x < live.length; x++) {
        const i = live[x] as number, v = (cnt[i] as number) / (cap[i] as number) - (first && items[i]?.priority ? 1e3 : 0);
        if (v < bv) { bv = v; li = x; }
      }
      const o = live[li] as number, qu = queues[o] as number[];
      if ((cnt[o] as number) >= (cap[o] as number) || (head[o] as number) >= qu.length) { live.splice(li, 1); continue; }
      const k = qu[head[o] as number] as number;
      head[o] = (head[o] as number) + 1;
      const m = nbrs4(g, k, nb);
      for (let t = 0; t < m; t++) {
        const q = nb[t] as number;
        if ((owner[q] as number) >= 0) continue;
        const cl = cls[q];
        if (cl === CLS_LAND) { if ((depth[k] as number) + 1 > dMax || (cnt[o] as number) >= (cap[o] as number)) continue; }
        else if (cl === CLS_WATER) { if (!((wt[o] as number) > 0 && (wc[o] as number) < (wt[o] as number))) continue; wc[o] = (wc[o] as number) + 1; }
        else continue;
        owner[q] = o; cnt[o] = (cnt[o] as number) + 1; depth[q] = (depth[k] as number) + 1; qu.push(q);
      }
    }
  };
  const all = items.map((_, i) => i);
  const short = () => all.reduce((t, i) => t + Math.max(0, (reqC[i] as number) - (cnt[i] as number)) / (reqC[i] as number) * (items[i]?.priority ? 10 : 1), 0);
  const isShort = (i: number) => (cnt[i] as number) < (reqC[i] as number) * 0.97;
  // a zone whose frontage runs over the end of one route into the next grows in two places; keep its main piece
  // (the one on the road) and let it grow on from there
  const isFront = new Uint8Array(n);
  front.forEach(l => l.forEach(k => { isFront[k] = 1; }));
  const settle = () => {
    keepLargestPieces(g, owner, 1, undefined, isFront);
    cnt.fill(0); wc.fill(0);
    for (let k = 0; k < n; k++) { const o = owner[k] as number; if (o < 0) continue; cnt[o] = (cnt[o] as number) + 1; if (cls[k] === CLS_WATER) wc[o] = (wc[o] as number) + 1; }
    grow(all, base, true);
  };
  plant(seeds);
  grow(all, base, true);
  settle();
  // a zone boxed in (say on a thin strip between the path and the lake) gets the nearest stretch of frontage with
  // plenty of land behind it instead, and the zones are grown again; a few rounds, keeping the best try
  if (all.some(isShort)) {
    const posOf = new Map(A.map((b, x) => [b, x])), taken = new Set<number>();
    let cur = seeds, curSpans = spans, best = { owner: owner.slice(), depth: depth.slice(), cnt: cnt.slice(), wc: wc.slice(), short: short() };
    for (let round = 0; round < 3; round++) {
      const stuck = all.filter(isShort).sort((a, b) => Number(!!items[b]?.priority) - Number(!!items[a]?.priority) || (reqC[b] as number) - (reqC[a] as number));
      if (!stuck.length) break;
      const next = cur.map(l => l.slice()), nextSpans = curSpans.map(l => l.slice());
      // a stretch may be taken from a zone only if it keeps some, and only at an end of its frontage (a priority zone
      // may take one from the middle)
      const holder = new Map<number, number>();
      nextSpans.forEach((l, j) => l.forEach(key => holder.set(key, j)));
      const free = (key: number, i: number) => {
        const j = holder.get(key);
        if (j === undefined) return true;
        const l = nextSpans[j] as number[];
        return l.length >= 2 && (!!items[i]?.priority || l[0] === key || l[l.length - 1] === key) && !items[j]?.priority;
      };
      let moved = 0;
      for (const i of stuck) {
        const mine = nextSpans[i] as number[], at = mine.length ? mine.reduce((t, k) => t + (posOf.get(k >> 1) ?? 0), 0) / mine.length : 0;
        let pick = -1, bd = Infinity;
        for (let x = 0; x < A.length; x++) for (const sd of [0, 1]) {
          const key = (A[x] as number) * 2 + sd;
          if (taken.has(key) || mine.includes(key) || !free(key, i) || !(front[key] as number[]).length || (room[key] as number) < (reqC[i] as number) * 2) continue;
          const d = Math.abs(x - at);
          if (d < bd) { bd = d; pick = key; }
        }
        if (pick < 0) continue;
        taken.add(pick);
        moved++;
        const cells = new Set(front[pick] as number[]);
        for (const l of next) for (let t = l.length - 1; t >= 0; t--) if (cells.has(l[t] as number)) l.splice(t, 1);
        next[i] = [...cells];
        const j = holder.get(pick);
        if (j !== undefined) nextSpans[j] = (nextSpans[j] as number[]).filter(k => k !== pick);
        for (const k of nextSpans[i] as number[]) holder.delete(k);
        nextSpans[i] = [pick];
        holder.set(pick, i);
      }
      if (!moved) break;
      plant(next);
      grow(all, base, true);
      settle();
      const sh = short();
      if (sh >= best.short) break;
      cur = next; curSpans = nextSpans;
      best = { owner: owner.slice(), depth: depth.slice(), cnt: cnt.slice(), wc: wc.slice(), short: sh };
    }
    owner.set(best.owner); depth.set(best.depth); cnt.set(best.cnt); wc.set(best.wc);
  }
  grow(all, tgt, false);

  // round off single-cell steps with a majority filter (land only, so claimed shoreline stays), then keep the largest piece
  const labs = new Int32Array(9), pre = owner.slice();
  for (let pass = 0; pass < 2; pass++) {
    const L2 = owner.slice();
    for (let k = 0; k < n; k++) {
      if (cls[k] !== CLS_LAND) continue;
      const i = k % N, j = (k - i) / N;
      if (i === 0 || j === 0 || i === N - 1 || j === N - 1) continue;
      let m = 0;
      for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
        const q = k + dj * N + di;
        labs[m++] = cls[q] === CLS_LAND || cls[q] === CLS_WATER ? owner[q] as number : -1;
      }
      let best = owner[k] as number, bc = 0;
      for (let a = 0; a < 9; a++) {
        let cn = 0;
        for (let b = 0; b < 9; b++) if (labs[b] === labs[a]) cn++;
        if (cn > bc) { bc = cn; best = labs[a] as number; }
      }
      if (bc >= 5 && best !== owner[k]) L2[k] = best;
    }
    owner.set(L2);
  }
  // a zone that met its path only at a corner can lose that corner to the filter; it gets back what the filter cleared
  const onRoad = new Uint8Array(items.length), wasOnRoad = new Uint8Array(items.length);
  for (let k = 0; k < n; k++) if (isFront[k]) { const a = pre[k] as number, b = owner[k] as number; if (a >= 0) wasOnRoad[a] = 1; if (b >= 0) onRoad[b] = 1; }
  for (let k = 0; k < n; k++) { const a = pre[k] as number; if (a >= 0 && wasOnRoad[a] && !onRoad[a] && (owner[k] as number) < 0) owner[k] = a; }
  // keep each zone's largest piece, preferring one that still has road frontage (so it stays reachable)
  keepLargestPieces(g, owner, i => Math.min(MIN_PIECE_CELLS, Math.max(4, Math.ceil((items[i] as PackItem).need / 2))), undefined, isFront);
  return { lab: owner, problem: '' };
}
